/**
 * src/lib/hooks/useChat.js — Unified chat state hook for the agent shell.
 *
 * Unified chat state: messages, input, scope, streaming, persistence, archive/
 * restore/rename. Built on top of useChatStream (82-01 SSE) and the
 * agent.js API module (Supabase-native reads).
 *
 * Phase 82-02 — Chat System Unification, data foundation.
 *
 * The scope contract (scopeContract.js) is the single source of truth for
 * entity shape. FK columns on chat_conversations (project_id, deal_id,
 * task_id, assigned_person_id) take precedence; freeform entries spill into
 * the `scope` jsonb via toConversationPatch.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabase';
import {
  fetchMessages,
  createConversation,
  updateConversationTitle,
  updateScope as apiUpdateScope,
  deleteConversation,
  restoreConversation,
  appendMessage,
} from '../api/agent';
import {
  fromConversationRow,
  deriveCtx,
  dedupeScope,
} from '../agent/scopeContract';
import { useChatStream } from './useChatStream';
import { logAppError } from '../errorReporter';
import { logAction, logResult } from '../observability';

// 2026-04-20 — audit P1-01: silent catches on chat data path previously emitted
// console.warn only. Now they log to app_error_logs via chat_error so SQL can
// pivot `metadata->>'step'` = hydrate|create|append|rename|persist_scope|rename_title.
function logChatError(step, err, metadata = {}) {
  logAppError({
    type: 'chat_error',
    message: `[useChat] ${step}: ${err?.message || String(err)}`,
    stack: err?.stack,
    view: 'AgentShell',
    metadata: { area: 'chat', step, ...metadata },
  });
}

const TITLE_MAX_WORDS = 6;

function firstWords(text, max = TITLE_MAX_WORDS) {
  const clean = (text || '').trim().replace(/\s+/g, ' ');
  if (!clean) return 'New chat';
  const words = clean.split(' ').slice(0, max);
  let title = words.join(' ');
  if (clean.split(' ').length > max) title += '…';
  return title;
}

/**
 * @param {object} options
 * @param {string|null} [options.conversationId]
 * @param {Array}       [options.initialScope]   — pre-seeded chips (from invocation context)
 * @param {string}      [options.view]           — echoed into send() for view-scoped prompts
 * @param {string}      [options.messageType]    — 'agent' | 'assistant' forwarded to useChatStream
 */
export function useChat({
  conversationId = null,
  initialScope = [],
  view,
  messageType = 'agent',
} = {}) {
  const [scope, setScopeState] = useState(() => dedupeScope(initialScope));
  const [title, setTitle] = useState('');
  const [archivedAt, setArchivedAt] = useState(null);
  const [hydrating, setHydrating] = useState(Boolean(conversationId));
  const [hydrationError, setHydrationError] = useState(null);
  const activeIdRef = useRef(conversationId);
  // 2026-04-20 Phase 5 (audit P1-4) — monotonic scope version. Increments
  // on every scope mutation; send() freezes the current version + a
  // shallow copy of scope entries so the server can detect stale scope
  // (entity archived between pick and send) and the request sent matches
  // what the user had pinned at click time.
  const scopeVersionRef = useRef(0);
  // Holds the user's chosen title for an unsaved conversation so the first
  // send uses it instead of firstWords(msg). Cleared after the creating
  // send commits.
  const pendingTitleRef = useRef(null);
  // Monotonic counter for append-refetch calls. Increments on each dispatch
  // so a stale earlier fetch can be discarded if a newer one has resolved.
  const appendFetchSeqRef = useRef(0);

  const stream = useChatStream({
    messageType,
    onSessionCreated: null,
    onStreamComplete: null,
    onError: null,
  });

  const {
    messages, setMessages,
    input, setInput,
    loading,
    sessionId, setSessionId,
    error,
    sendMessage: streamSend,
    stopGeneration,
    clearMessages,
  } = stream;

  useEffect(() => {
    activeIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    let cancelled = false;

    if (!conversationId) {
      setScopeState(prev => dedupeScope(prev.length ? prev : initialScope));
      setTitle('');
      setArchivedAt(null);
      setHydrating(false);
      clearMessages();
      return () => {
        cancelled = true;
      };
    }

    setHydrating(true);
    setHydrationError(null);
    const hydrateCorrelationId = logAction('chat.hydrate', { conversationId });

    (async () => {
      try {
        const { data: row, error: convErr } = await supabase
          .from('chat_conversations')
          .select('id, title, project_id, deal_id, task_id, assigned_person_id, scope, archived_at, created_at, updated_at')
          .eq('id', conversationId)
          .maybeSingle();

        if (convErr) throw new Error(convErr.message);
        if (cancelled) return;

        const msgs = await fetchMessages(conversationId);
        if (cancelled) return;

        const hydratedScope = row ? fromConversationRow(row) : [];
        const mergedScope = hydratedScope.length ? hydratedScope : dedupeScope(initialScope);

        setScopeState(mergedScope);
        setTitle(row?.title || '');
        setArchivedAt(row?.archived_at || null);

        setMessages(
          (msgs || []).map(m => ({
            type: m.role === 'user' ? 'user' : messageType,
            text: m.content,
            id: m.id,
            toolChips: Array.isArray(m.tool_calls) ? m.tool_calls : [],
            created_at: m.created_at,
          }))
        );
        setSessionId(conversationId);
        logResult('chat.hydrate', 'success', {
          correlationId: hydrateCorrelationId,
          conversationId,
          message_count: (msgs || []).length,
        });
      } catch (err) {
        if (!cancelled) setHydrationError(err.message || String(err));
        logChatError('hydrate', err, { conversationId, correlationId: hydrateCorrelationId });
        logResult('chat.hydrate', 'error', { correlationId: hydrateCorrelationId, conversationId });
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
      stopGeneration();
    };
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const ctx = useMemo(() => deriveCtx(scope), [scope]);

  const persistScope = useCallback(async (nextScope, cid) => {
    if (!cid) return;
    // 2026-04-20 diagnostic layer — the CEO has been reporting "Deal/Task/
    // Person pills don't land" intermittently. Unit tests pass end-to-end
    // (picker → onAdd → addScopeEntry → setScope → persistScope), which
    // means the failure is at the Supabase RLS/FK boundary. The previous
    // code swallowed errors; now we also emit a voice_error row so the
    // voice-monitor script surfaces the actual message and Chris can see
    // WHY a pill didn't land without opening devtools on mobile.
    const intent = logAction('chat:persist_scope', {
      conversationId: cid,
      scopeCount: nextScope?.length || 0,
      types: (nextScope || []).map(e => e?.type).filter(Boolean),
    });
    try {
      await apiUpdateScope({ conversationId: cid, scope: nextScope });
      logResult('chat:persist_scope', 'success', { correlationId: intent, conversationId: cid });
    } catch (err) {
      console.error('[useChat] persistScope failed:', err?.message, {
        conversationId: cid,
        scope: nextScope,
      });
      logChatError('persist_scope', err, { conversationId: cid });
      logResult('chat:persist_scope', 'error', {
        correlationId: intent,
        conversationId: cid,
        error: err?.message || String(err),
      });
    }
  }, []);

  const setScope = useCallback((nextOrUpdater) => {
    setScopeState(prev => {
      const next = dedupeScope(
        typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      );
      // Phase 5: bump only when the scope actually changed — skipping
      // identity-only updates prevents the version from drifting on pure
      // hydration re-sets.
      const changed = next.length !== prev.length
        || next.some((e, i) => !prev[i] || e.type !== prev[i].type || e.id !== prev[i].id);
      if (changed) scopeVersionRef.current += 1;
      const cid = activeIdRef.current;
      if (cid) persistScope(next, cid);
      return next;
    });
  }, [persistScope]);

  const addScopeEntry = useCallback((entry) => {
    if (!entry || !entry.type || !entry.id) return;
    setScope(prev => [...prev, entry]);
  }, [setScope]);

  const removeScopeEntry = useCallback((type, id) => {
    setScope(prev => prev.filter(e => !(e.type === type && e.id === id)));
  }, [setScope]);

  const send = useCallback(async (text, opts = {}) => {
    const msg = (text || '').trim();
    if (!msg || loading) return;

    let cid = activeIdRef.current;

    if (!cid) {
      const createCorrelationId = logAction('chat.create', {
        hasProjectScope: Boolean(scope.find(e => e.type === 'project')),
      });
      try {
        const firstProject = scope.find(e => e.type === 'project');
        const created = await createConversation(firstProject?.id || null);
        cid = created?.id;
        if (cid) {
          activeIdRef.current = cid;
          if (scope.length) await persistScope(scope, cid);
          // Honor a pre-set draft title (user renamed the chat before
          // sending the first message). Falls back to firstWords derivation.
          const chosenTitle = pendingTitleRef.current || firstWords(msg);
          await updateConversationTitle(cid, chosenTitle);
          setTitle(chosenTitle);
          pendingTitleRef.current = null;
          if (opts.onConversationCreated) opts.onConversationCreated(cid);
          logResult('chat.create', 'success', { correlationId: createCorrelationId, conversationId: cid });
        } else {
          logResult('chat.create', 'no_id_returned', { correlationId: createCorrelationId });
        }
      } catch (err) {
        logChatError('create', err, { correlationId: createCorrelationId });
        logResult('chat.create', 'error', { correlationId: createCorrelationId });
      }
    }

    // Phase 5 (audit P1-4): freeze scope + version at send start. Any
    // setScope() mutation that lands AFTER this line cannot affect the
    // request — guarantees "the reply matches what the user clicked send
    // on." The frozen scopeEntries are forwarded so the server can verify
    // no referenced entity has been archived between pick and send.
    const frozenScope = [...scope];
    const frozenScopeVersion = scopeVersionRef.current;
    const projectEntry = frozenScope.find(e => e.type === 'project');
    const dealEntry = frozenScope.find(e => e.type === 'deal');
    const taskEntry = frozenScope.find(e => e.type === 'task');
    const personEntry = frozenScope.find(e => e.type === 'person');

    if (cid) {
      appendMessage({ conversationId: cid, role: 'user', content: msg })
        .catch(err => logChatError('append_user', err, { conversationId: cid }));
    }

    // Forward ALL scope-entry types to the agent. Previously only project_id
    // and deal_id were wired, so clicking a Task or Person chip showed the
    // pill in the header but the agent had no idea what the user had pinned
    // — same pattern the CEO flagged as "filters on top of pills engage well
    // and sync well together with the bundle and the context of the chat."
    // Task carries its project_id in the scope entry, so the server can
    // resolve the owning bundle + project from the task alone.
    //
    // 2026-04-20 Phase 2 (audit P1-1): streamSend now returns the final
    // assistant payload. Persist by value (correlation-based) instead of
    // reading the tail of the messages array with beforeLen — the old
    // approach broke under overlap/race, because another setMessages
    // landing between the await start and end shifted the slice index.
    const result = await streamSend(msg, {
      conversationId: cid || undefined,
      projectId: projectEntry?.id,
      dealId: dealEntry?.id,
      taskId: taskEntry?.id,
      personId: personEntry?.id,
      view: opts.view ?? view,
      provider: opts.provider,
      scopeVersion: frozenScopeVersion,
      scopeEntries: frozenScope.map(e => ({ type: e.type, id: e.id })),
    });

    if (cid && result && !result.aborted && result.text) {
      appendMessage({
        conversationId: cid,
        role: 'assistant',
        content: result.text,
        tool_calls: result.toolChips || null,
      }).catch(err => logChatError('append_assistant', err, {
        conversationId: cid,
        requestId: result.requestId,
      }));
    }
  }, [loading, scope, streamSend, view, messageType, persistScope]);

  const retry = useCallback(() => {
    if (loading) return;
    let lastUserIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'user') { lastUserIdx = i; break; }
    }
    if (lastUserIdx < 0) return;
    const text = messages[lastUserIdx].text;
    setMessages(prev => prev.slice(0, lastUserIdx));
    return send(text);
  }, [messages, loading, send, setMessages]);

  const archive = useCallback(async () => {
    const cid = activeIdRef.current;
    if (!cid) return null;
    const row = await deleteConversation(cid);
    if (row) setArchivedAt(row.archived_at || new Date().toISOString());
    return row;
  }, []);

  const restore = useCallback(async () => {
    const cid = activeIdRef.current;
    if (!cid) return null;
    const row = await restoreConversation(cid);
    if (row) setArchivedAt(null);
    return row;
  }, []);

  const rename = useCallback(async (nextTitle) => {
    const clean = (nextTitle || '').trim();
    if (!clean) return null;
    const cid = activeIdRef.current;
    // 2026-04-20 — for an unsaved new chat (cid === null), persist the
    // draft title locally + stash in pendingTitleRef. The first send()
    // will use this name when it creates the conversation instead of
    // auto-deriving from the first user words. Was silently bailing
    // before, which is what the CEO saw as "I can't change the title
    // of the new chat."
    if (!cid) {
      pendingTitleRef.current = clean;
      setTitle(clean);
      return { id: null, title: clean, pending: true };
    }
    const correlationId = logAction('chat.rename', { conversationId: cid });
    try {
      const row = await updateConversationTitle(cid, clean);
      if (row) setTitle(row.title || clean);
      logResult('chat.rename', 'success', { correlationId, conversationId: cid });
      return row;
    } catch (err) {
      logChatError('rename', err, { conversationId: cid, correlationId });
      logResult('chat.rename', 'error', { correlationId, conversationId: cid });
      throw err;
    }
  }, []);

  // Listen for sidebar-driven renames so the header title updates without
  // requiring a full conversation re-hydration (conversationId didn't change).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const { id, title: newTitle } = e.detail || {};
      if (id && id === activeIdRef.current && newTitle) {
        setTitle(newTitle);
      }
    };
    window.addEventListener('chat-conversation-renamed', handler);
    return () => window.removeEventListener('chat-conversation-renamed', handler);
  }, []);

  // Bug 1 (2026-06-07) — voice conversation turns are written to the same
  // chat_messages table (appendMessage source='voice' in useVoiceConversation),
  // but the hydrate effect above only re-reads rows when conversationId
  // CHANGES. An already-mounted main chat therefore never sees the rows voice
  // inserted into the SAME conversation. Mirror the rename listener: on a
  // `chat-messages-appended` event targeting the active conversation, refetch
  // and re-map exactly as the hydrate effect does. Events for other
  // conversations are ignored.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const cid = e.detail?.conversationId;
      if (!cid || cid !== activeIdRef.current) return;
      // Capture the sequence number at dispatch time; discard the result if a
      // newer fetch has already resolved (two rapid events, first resolves last).
      const seq = ++appendFetchSeqRef.current;
      (async () => {
        try {
          const msgs = await fetchMessages(cid);
          // Guard against a conversation switch landing mid-fetch.
          if (cid !== activeIdRef.current) return;
          // Guard against a stale earlier fetch overwriting a newer result.
          if (seq !== appendFetchSeqRef.current) return;
          setMessages(
            (msgs || []).map(m => ({
              type: m.role === 'user' ? 'user' : messageType,
              text: m.content,
              id: m.id,
              toolChips: Array.isArray(m.tool_calls) ? m.tool_calls : [],
              created_at: m.created_at,
            }))
          );
        } catch (err) {
          logChatError('append_refetch', err, { conversationId: cid });
        }
      })();
    };
    window.addEventListener('chat-messages-appended', handler);
    return () => window.removeEventListener('chat-messages-appended', handler);
  }, [messageType, setMessages]);

  const stop = useCallback(() => {
    stopGeneration();
  }, [stopGeneration]);

  return {
    messages,
    input, setInput,
    scope, setScope, addScopeEntry, removeScopeEntry,
    ctx,
    title, rename,
    archivedAt, archive, restore,
    send, stop, retry,
    clearMessages,
    loading,
    hydrating,
    error: error || hydrationError,
    sessionId,
  };
}
